import axiosInstance from 'config/axiosInstance';
import { PAGE_SIZE } from 'config/constant';
import React, { useCallback, useEffect, useState } from 'react';
import Pagination from 'reusable/pagination';
import SortBy from 'reusable/sortby';
import video_icon from '../../../../assets/icons/video.svg';
import VideoModal from 'reusable/videopreview';
import DateRangeFilter from 'reusable/datefilterrange';
import { useTranslation } from 'react-i18next';

const sortingOptions = [
  { label: 'Default', value: '' },
  { label: 'Submit Time', value: 'testing_time' },
];

interface DriverAlcoholSubmissionsTableProps {
  id?: string;
}

interface DriverAlcoholSubmission {
  user?: {
    name: string;
    phone: string;
  };
  data: string;
  evidence: string;
  testing_time: string;
  location_name: string;
  memo: string;
}

const DriverAlcoholSubmissionsTable: React.FC<
  DriverAlcoholSubmissionsTableProps
> = ({ id }) => {
  const { t, i18n } = useTranslation('driver');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortValue, setSortValue] = useState('');
  const [count, setCount] = useState(0);
  const [driverAlcoholSubmissions, setDriverAlcoholSubmissions] = useState<DriverAlcoholSubmission[]>([]);
  const [showVideo, setShowVideo] = useState(false);
  const [currentVideoLink, setCurrentVideoLink] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
  };

  const formatDate = (datetime: string) => {
    const date = new Date(datetime);
    
    if (i18n.language === 'jp') {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); 
        const year = date.getFullYear();

        return {
            time: date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', hour12: false }),
            date: `${year}/${month}/${day}`
        };
    } else {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const day = date.getDate().toString().padStart(2, '0'); 
        const monthIndex = date.getMonth();
        const year = date.getFullYear();
        const localTimeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

        return {
            time: localTimeString,
            date: `${day} ${months[monthIndex]} ${year}`
        };
    }
  };
  const handleVideoClick = (videoLink: string) => {
    if (videoLink) {
      setCurrentVideoLink(videoLink);
      setShowVideo(true);
    } else {
      console.log('No video available');
    }
  };
  const getColorClass = (dataValue: string) => {
    const value = parseFloat(dataValue);
    if (value >= 0.15) {
      return 'text-red-500 bg-red-100 px-2 rounded-md flex items-center justify-center w-24';
    } else if (value <= 0.0) {
      return 'text-green-500 bg-green-100 px-2 rounded-md flex items-center justify-center w-24';
    } else {
      return 'text-yellow-500 bg-yellow-100 px-2 rounded-md flex items-center justify-center w-24';
    }
  };

  const getFileExtension = (url: string) => {
    const extension = url.split('.').pop();
    if (extension) {
      return extension.split('?')[0];
    }
    return '';
  };

  const fetchData = useCallback(async () => {
    try {
      let apiUrl = '/v1/alcohol_tests/?';
      if (dateRange.startDate && dateRange.endDate) {
        apiUrl += `testing_time__gte=${dateRange.startDate}T00%3A00%3A00.000000Z&testing_time__lte=${dateRange.endDate}T23%3A59%3A59.999999Z&`;
      }
      apiUrl += `order_by=${sortValue}&page=${currentPage}&page_size=${PAGE_SIZE}&user=${id}`;
      const response = await axiosInstance.get(apiUrl);
      setDriverAlcoholSubmissions(response.data.results);
      setCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  }, [id, currentPage, sortValue, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <section className="flex flex-col space-y-2">
      <div className="flex flex-row space-x-2 items-center justify-between">
        {/* ToolBar Section */}
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />

        <DateRangeFilter onDateRangeChange={handleDateRangeChange} />
      </div>
      {/* Table */}
      <div>
        <div className="flex flex-row bg-primary/50 rounded-md py-2 px-4">
          <h6 className="flex-1">{t('view.alcohol-submission.submit-date')}</h6>
          <h6 className="flex-1">{t('view.alcohol-submission.submit-time')}</h6>
          <h6 className="flex-1 text-center">{t('view.alcohol-submission.test-result')}</h6>
          <h6 className="flex-1 text-center">{t('view.alcohol-submission.video-submission')}</h6>
          <h6 className="flex-1 text-center">{t('view.alcohol-submission.last-location')}</h6>
        </div>
        <div>
          {showVideo && (
            <VideoModal
              videoLink={currentVideoLink}
              onClose={() => setShowVideo(false)}
            />
          )}
          {driverAlcoholSubmissions.map((submission, index) => (
            <div
              key={index}
              className="grid grid-cols-5 gap-4 px-4 py-3 border-b"
            >
              <div className="flex-1">
                {formatDate(submission.testing_time).date}
              </div>
              <div className="flex-1">
                {formatDate(submission.testing_time).time}
              </div>
              <div className="flex flex-1 items-center justify-center">
                <div className={`${getColorClass(submission.data)}`}>
                  {submission.data} mg/L
                </div>
              </div>
              <div
                className={`flex-1 border flex items-center rounded-md justify-center ${
                  submission.evidence ? 'cursor-pointer' : ''
                }`}
                onClick={() =>
                  submission.evidence && handleVideoClick(submission.evidence)
                }
              >
                {submission.evidence ? (
                  <>
                    <img src={video_icon} alt="Video Icon" className="mr-2" />
                    {`${submission.evidence.slice(-5)} (.${getFileExtension(
                      submission.evidence
                    )})`}
                  </>
                ) : (
                  <span className="text-gray-500">No Video</span>
                )}
              </div>
              <div className="flex-1 inline-block truncate px-4 text-center underline text-primary/300">
                {submission.location_name}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Pagination Section */}
      <div>
        <Pagination
          allRows={count}
          rowsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </section>
  );
};

export default DriverAlcoholSubmissionsTable;